import React from 'react'

// Presentation only - every Section/SubItem call below (and all of its text) is unchanged from
// before; only these two building blocks were restyled, to read like a standard SaaS legal
// document (numbered clauses, generous spacing, muted body copy) instead of plain unstyled text.
function TermsAndConditions() {

    const Section = ({ number, title, children }) => (
      <section className="pt-7 pb-1 mb-2 border-t border-navy/10 first:border-t-0 first:pt-0">
        <div className="flex items-baseline gap-3 mb-4">
          <span className="font-plexmono text-[11.5px] tracking-[0.08em] text-tan flex-none">{number}</span>
          <h2 className="font-spectral font-normal text-navy text-[17px] lg:text-[19px] leading-snug m-0">
            {title}
          </h2>
        </div>
        <div className="flex flex-col gap-3.5">{children}</div>
      </section>
    );

    const SubItem = ({ number, title, children }) => (
      <div className="flex gap-3">
        <span className="font-plexmono text-[10.5px] text-navy/40 flex-none pt-[3px] whitespace-nowrap">{number}</span>
        <div className="flex-1 min-w-0 flex flex-col gap-3.5">
          {title ? <p className="font-plexsans font-semibold text-navy text-[13px] lg:text-[13.5px] m-0">{title}</p> : null}
          <div
            className="font-plexsans text-navy/70 text-[12.5px] lg:text-[13px] leading-[1.7] flex flex-col gap-3.5
              [&_ul]:list-none [&_ul]:pl-0 [&_ul]:m-0 [&_ul]:flex [&_ul]:flex-col [&_ul]:gap-1.5
              [&_li]:leading-[1.7]"
          >
            {children}
          </div>
        </div>
      </div>
    );
  return (
    <div className="font-plexsans text-[12.5px] lg:text-[13px] leading-[1.7]">
    <p className="mb-6 text-navy/75">
  These Terms and Conditions (“Terms”) govern use, by any user, including attorneys,
  legal assistants, leads, visitors ("User"), of the website,{' '}
  <a href="https://www.quickcasereview.com" className="text-tan underline underline-offset-2 hover:text-tanDark">
    https://www.quickcasereview.com
  </a>{' '}
  (the “Site”), and the services provided (collectively, the “Services”) by the Site. By
  accessing or using our Site or Services, you agree to be bound by these Terms. If you do
  not agree, please do not use our Site or Services. The Terms were last updated on April 5,
  2025.
    </p>

    <Section number="1" title="Nature of Services">
      <SubItem number="1.1" title="Lead Organization Only">
        Quick Case Review ("Company") operates a web-based platform
        ("Platform") that connects potential clients ("Leads") with licensed attorneys
        ("Attorneys"). The Platform is solely for the purpose of lead organization and does not
        provide legal services.
      </SubItem>
      <SubItem number="1.2" title="No Attorney-Client Relationship">
        Use of the Platform does not create an attorney-client relationship between Company and
        any Lead or Attorney. Company is not a law firm and does not provide legal advice,
        representation, or services.
      </SubItem>
      <SubItem number="1.3" title="Independent Contractors">
        Attorneys using the Platform are independent contractors and not employees or agents of
        Company. Company does not supervise, direct, or control Attorneys' work or legal services
        provided to Leads.
      </SubItem>
      <SubItem number="1.4" title="No Solicitation">
        Company does not enable an Attorney to solicit a Lead using the Site or initiate
        communication with a Lead using the Site. Company enables a Lead to initiate communication
        with Attorneys on the Site.
      </SubItem>
    </Section>

        <Section number="2.2" title="Lead Obligations">
          <SubItem number="2.2.1" title="No Legal Advice.">
          Advice. Leads acknowledge that Company does not provide legal advice and that use of
  the Platform does not constitute legal representation.
          </SubItem>
          <SubItem number="2.2.2" title="Direct Communication.">
          Leads agree to interact with Attorneys regarding legal matters and not rely
  on Company for legal assistance.
          </SubItem>
          <SubItem number="2.2.3" title="Leads">
          Leads, including leads registered with the Site and unregistered with the Site, agree to the Terms.
          </SubItem>
        </Section>
        <Section number="3" title="Platform Limitations">
    <SubItem number="3.1" title="Information Only.">
      The Platform provides information about Attorneys and potential legal matters but does not offer legal advice or services.
    </SubItem>
    <SubItem number="3.2" title="No Guarantees.">
      Company does not guarantee the quality, accuracy, or suitability of Attorneys or their services. Leads are responsible for evaluating and selecting Attorneys.
    </SubItem>
    <SubItem number="3.3" title="Disclaimer of Liability.">
      Company is not liable for any actions, omissions, or advice of Attorneys or for any legal outcomes resulting from connections made through the Platform.
    </SubItem>
  </Section>

  <Section number="4" title="Fees and Payments">
    <SubItem number="4.1" title="Reasonable Data Management Fees and/or Reasonable Attorney Advertising Fees.">
      Attorneys agree to pay Company reasonable fees for data management services and/or advertising services, which may be updated from time to time.
    </SubItem>
    <SubItem number="4.2" title="No Legal Fee Sharing.">
      Company does not share in legal fees earned by Attorneys from Leads. All fee arrangements are between Attorneys and Leads directly.
    </SubItem>
  </Section>
  <Section number="5" title="Confidentiality and Data Protection">
<SubItem number="5.1" title="Lead Information.">
Company will maintain the confidentiality of Lead information and share it only with Attorneys as necessary for lead organization and case advertising purposes.
</SubItem>
<SubItem number="5.2" title="Attorney Information.">
Company will protect Attorney information and use it solely for Platform operations, advertising operations, and lead organization.
</SubItem>
</Section>

<Section number="6" title="Legal Assistant Registration and Authorization">
<SubItem number="6.1" title="Legal Assistant Registration">
<SubItem number="6.1.1" title="">
Legal Assistants may register with the Platform to respond to leads and perform actions on behalf of attorneys.
</SubItem>
<SubItem number="6.1.2" title="">
Legal Assistants must provide accurate registration information, including their full name, contact details, and employing law firm.
</SubItem>
<SubItem number="6.1.3" title="">
Legal Assistants, including Legal Assistants registered with the Site and unregistered with the Site, agree to the Terms.
</SubItem>
</SubItem>

<SubItem number="6.2" title="Attorney Approval and Authorization">
<SubItem number="6.2.1" title="">
A Legal Assistant may only act on the Platform on behalf of Attorneys who have expressly approved and authorized the Legal Assistant to do so.
</SubItem>
<SubItem number="6.2.2" title="">
Attorneys must formally designate which Legal Assistants are authorized to act on their behalf through the Platform's authorization system.
</SubItem>
<SubItem number="6.2.3" title="">
An Attorney may revoke a Legal Assistant's authorization at any time through the Platform.
</SubItem>
</SubItem>

<SubItem number="6.3" title="Scope of Legal Assistant Authority">
  <SubItem number="6.3.1" title="">
    Legal Assistants may only act on behalf of Attorneys employed by the same law firm as the Legal Assistant.
  </SubItem>
  <SubItem number="6.3.2" title="">
    A Legal Assistant may be authorized to act on behalf of multiple Attorneys within the same law firm.
  </SubItem>
  <SubItem number="6.3.3" title="">
    The scope of a Legal Assistant's authority on the Platform is limited to:
    <ul>
      <li>a. Responding to potential leads</li>
      <li>b. Interacting with leads on behalf of authorized attorneys</li>
      <li>c. Updating lead information and status</li>
      <li>d. Performing administrative tasks related to lead management</li>
    </ul>
  </SubItem>
  <SubItem number="6.3.4" title="">
    Legal Assistants shall not establish attorney-client relationships, set legal fees, or provide legal advice independently of the Attorney they are authorized to act on behalf of.
  </SubItem>
</SubItem>

<SubItem number="6.4" title="Responsibility and Liability">
  <SubItem number="6.4.1" title="">
    Attorneys remain fully responsible and liable for all actions taken by their authorized Legal Assistants on the Platform.
  </SubItem>
  <SubItem number="6.4.2" title="">
    Legal Assistants must comply with all applicable laws, regulations, and ethical standards when acting on behalf of attorneys.
  </SubItem>
  <SubItem number="6.4.3" title="">
    Company is not responsible for any unauthorized actions taken by Legal Assistants or for any disputes between Attorneys and Legal Assistants regarding Platform usage.
  </SubItem>
</SubItem>

<SubItem number="6.5" title="Confidentiality and Data Protection">
  <SubItem number="6.5.1" title="">
    Legal Assistants must maintain the confidentiality of all lead information accessed through the Platform.
  </SubItem>
  <SubItem number="6.5.2" title="">
    Legal Assistants are prohibited from sharing lead information with unauthorized parties or using it for purposes outside the scope of their authorized duties.
  </SubItem>
</SubItem>
</Section>
<Section number="7" title="Lead Case Posting">
<SubItem number="7.1" title="Case Submission">
<SubItem number="7.1.1" title="">
Leads may submit details of their legal cases ("Case Information") to the Platform for advertising to Attorneys.
</SubItem>
<SubItem number="7.1.2" title="">
By submitting Case Information, Leads represent and warrant that all information provided is accurate, complete, and truthful to the best of their knowledge.
</SubItem>
<SubItem number="7.1.3" title="">
Leads are solely responsible for the content of their Case Information.
</SubItem>
</SubItem>

<SubItem number="7.2" title="No Attorney-Client Relationship">
<SubItem number="7.2.1" title="">
Posting Case Information on the Platform does not create an attorney-client relationship between the Lead and any Attorney or Company.
</SubItem>
<SubItem number="7.2.2" title="">
Leads acknowledge that Company does not provide legal advice or services.
</SubItem>
</SubItem>

<SubItem number="7.3" title="Confidentiality and Privacy">
<SubItem number="7.3.1" title="">
Company will maintain the confidentiality of Case Information and share it only with Attorneys on the Platform.
</SubItem>
<SubItem number="7.3.2" title="">
Leads should not include highly sensitive or privileged information in their Case Information submission.
</SubItem>
<SubItem number="7.3.3" title="">
Company implements reasonable security measures to protect Case Information but cannot guarantee absolute security.
</SubItem>
</SubItem>

<SubItem number="7.4" title="Case Visibility and Attorney Access">
<SubItem number="7.4.1" title="">
Case Information will be visible to Attorneys on the Platform for a specified period determined by Company.
</SubItem>
<SubItem number="7.4.2" title="">
Leads may remove Case Information at any time.
</SubItem>
<SubItem number="7.4.3" title="">
Company does not guarantee that any attorney will respond to or accept a Lead's case.
</SubItem>
</SubItem>

<SubItem number="7.5" title="Interaction with Attorneys">
<SubItem number="7.5.1" title="">
Leads are responsible for evaluating Attorneys and deciding whether to engage an Attorney's services.
</SubItem>
<SubItem number="7.5.2" title="">
Any agreement for legal services is solely between the Lead and their chosen Attorney.
</SubItem>
<SubItem number="7.5.3" title="">
Company does not provide any agreements for legal services between the Lead and their chosen Attorney.
</SubItem>
</SubItem>

<SubItem number="7.6" title="Prohibited Content">
<SubItem number="7.6.1" title="">
Leads shall not submit Case Information that:
<ul>
  <li>a) Is false, misleading, or fraudulent</li>
  <li>b) Violates any third party's rights</li>
  <li>c) Contains explicit or offensive material</li>
  <li>d) Promotes illegal activities</li>
</ul>
</SubItem>
<SubItem number="7.6.2" title="">
Company reserves the right to remove any Case Information that violates these terms or is otherwise objectionable.
</SubItem>
</SubItem>

<SubItem number="7.7" title="Limitation of Liability">
<SubItem number="7.7.1" title="">
Company is not responsible for the actions or omissions of Attorneys or Legal Assistants who access Case Information.
</SubItem>
<SubItem number="7.7.2" title="">
Company does not guarantee the quality, accuracy, or suitability of Attorney interaction with Case Information or Leads.
</SubItem>
<SubItem number="7.7.3" title="">
Leads use the Platform at their own risk and waive any claims against Company related to Attorney selection or representation.
</SubItem>
</SubItem>
</Section>
<Section number="8" title="Reasonable Fees for Usage of Site">
<SubItem number="8.1" title="Periodic Reasonable Data Management Fee">
<SubItem number="8.1.1" title="">
Attorneys shall pay a reasonable periodic fee ("Data Management Fee") to Company for data management services on the Platform.
</SubItem>
<SubItem number="8.1.2" title="">
The Data Management Fee shall be payable monthly in advance.
</SubItem>
<SubItem number="8.1.3" title="">
The current Data Management Fee schedule is available on the Platform and may be updated by Company from time to time with 1 day of notice.
</SubItem>
<SubItem number="8.1.4" title="">
The Data Management Fee may also include a reasonable attorney advertising fee for advertising services provided by the Site.
</SubItem>
</SubItem>

<SubItem number="8.2" title="Enhanced Services Provided">
<SubItem number="8.2.1" title="">
Payment of the Data Management Fee entitles Attorneys to:
<ul>
  <li>a) Access to some case leads</li>
  <li>b) Advanced filtering capabilities for lead searches</li>
  <li>c) Additional privileges not available to non-paying Attorneys</li>
</ul>
</SubItem>
<SubItem number="8.2.2" title="">
The specific enhanced services may be modified by Company from time to time, with notice provided to paying attorneys.
</SubItem>
</SubItem>

<SubItem number="8.3" title="Nature of Data Management Fee">
<SubItem number="8.3.1" title="">
The Data Management Fee is for enhanced data services and does not guarantee any attorney will receive leads or cases through the Platform.
</SubItem>
<SubItem number="8.3.2" title="">
Attorneys acknowledge that payment of the Data Management Fee does not entitle them to any specific number or quality of leads.
</SubItem>
</SubItem>

<SubItem number="8.4" title="Fee Payment and Account Status">
<SubItem number="8.4.1" title="">
Attorneys must maintain their account in good standing by timely payment of the Data Management Fee.
</SubItem>
<SubItem number="8.4.2" title="">
Failure to pay the Data Management Fee when due may result in immediate suspension or termination of the Attorney's access to enhanced services on the Platform.
</SubItem>
<SubItem number="8.4.3" title="">
Company reserves the right to charge late fees on overdue Data Management Fee payments.
</SubItem>
</SubItem>

<SubItem number="8.5" title="Refunds and Credits">
<SubItem number="8.5.1" title="">
Data Management Fees are non-refundable, except as required by applicable law.
</SubItem>
<SubItem number="8.5.2" title="">
No full or partial refunds will be issued for any period during which an attorney chooses not to access the enhanced services or is suspended for violation of the Agreement.
</SubItem>
</SubItem>

<SubItem number="8.6" title="Relationship to Legal Fees">
<SubItem number="8.6.1" title="">
Attorneys are solely responsible for setting and collecting their own legal fees from clients, i.e., converted leads, obtained through the Platform.
</SubItem>
<SubItem number="8.6.2" title="">
Company does not receive any portion of legal fees charged by attorneys to clients.
</SubItem>
<SubItem number="8.6.3" title="">
Attorneys shall not increase their legal fees to clients to offset the cost of the Data Management Fee.
</SubItem>
</SubItem>
</Section>
<Section number="9" title="Jurisdictional Limitations">
<SubItem number="9.1" title="">
The Platform is restricted to attorneys barred in any of the states of the United States.
</SubItem>
<SubItem number="9.2" title="">
Case Information should relate to potential cases that fall within the jurisdiction of the United States, or any of the states thereof, including state, federal, or administrative bodies, to be eligible for posting on the Platform.
</SubItem>
</Section>

<Section number="10" title="Disclaimer of Liability">
<SubItem number="10.1" title="">
The Platform does not guarantee legal representation or case outcomes.
</SubItem>
<SubItem number="10.2" title="">
Users, including Attorneys, Legal Assistants, Leads, and other visitors, agree to indemnify and hold Company harmless from any claims arising from their use of the services provided by the Platform.
</SubItem>
</Section>

<Section number="11" title="User Responsibilities">
<SubItem number="11.1" title="Accurate Information">
Users agree to provide true and accurate information when using the Site. False or misleading information may result in termination of User's access to the Site.
</SubItem>
<SubItem number="11.2" title="No Misuse">
Users may not use the Site for any unlawful purpose, to harass others, or to interfere with its operation (e.g., through hacking, spamming, or submitting malicious content).
</SubItem>
</Section>

<Section number="12" title="Privacy and Data Use">
<SubItem number="12.1" title="">
Users' personal information is handled according to our Privacy Policy, which is incorporated into these Terms. By using the Services, you consent to the collection, use, and sharing of your information as described therein.
</SubItem>
</Section>

<Section number="13" title="Intellectual Property">
<SubItem number="13.1" title="">
All content on the Site (e.g., text, logos, designs, computing functionality, computing communications, computing interfaces, etc.) is protected by patents (both pending and granted), trademarks, copyright and other laws. 
</SubItem>
<SubItem number="13.2" title="">
Users, including registered Users or unregistered Users, may use the Site for personal, non-commercial purposes only. Users agree to not copy, modify, distribute, reproduce, or infringe upon any content or intellectual property comprised in or protecting the content.
</SubItem>
<SubItem number="13.3" title="">
This section applies to any visitors to the Site, including Attorneys, Leads, Legal Assistants, or other visitors who are merely browsing any of the content on the Site.
</SubItem>
</Section>

<Section number="14" title="Limitation of Liability">
<SubItem number="14.1" title="">
To the fullest extent permitted by law, Company is not liable for any direct, indirect, incidental, or consequential damages arising from: User's use of the Site or Services; Any actions or inactions of Attorneys or third parties; Errors, interruptions, or inaccuracies on the Site. 
</SubItem>
<SubItem number="14.2" title="">
Company's total liability, if any, will not exceed $100.
</SubItem>
</Section>

<Section number="15" title="Indemnification">
<SubItem number="15.1" title="">
Users agree to defend, indemnify, and hold harmless Company, its affiliates, and their respective officers, employees, and agents from any claims, losses, or damages (including legal fees) arising from Users' use of the Site, violation of these Terms, or submission of inaccurate information.
</SubItem>
</Section>

<Section number="16" title="Third-Party Links">
<SubItem number="16.1" title="">
The Site may contain links to third-party websites (e.g., Attorney websites, etc.). Company is not responsible for the content, accuracy, or practices of these sites. Accessing them is at User's own risk.
</SubItem>
</Section>
<Section number="17" title="Termination">
<SubItem number="17.1" title="">
Company may suspend or terminate your access to the Site at our discretion, with or without notice, for any reason, including if you violate these Terms. Sections 12–15 and 18–19 will survive termination.
</SubItem>
</Section>

<Section number="18" title="Changes to These Terms">
<SubItem number="18.1" title="">
Company may update these Terms from time to time. Changes will be posted on this page with an updated “Last Updated” date. User's continued use of the Site after changes constitutes acceptance of the new Terms.
</SubItem>
</Section>

<Section number="19" title="Governing Law">
<SubItem number="19.1" title="">
These Terms are governed by the laws of Texas, without regard to conflict of law principles. Any disputes will be resolved exclusively in the state or federal courts located in Denton County, Texas.
</SubItem>
</Section>

   </div>

  )
}

export default TermsAndConditions
